import {
  createStyles,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  TableCell,
  TableRow,
  TextField,
  Theme,
} from '@material-ui/core';
import { Cancel, Check, Delete, Edit } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { AnalysisConfigurationGq } from '../../../graphql/projects/models/AnalysisConfigurationGq';
import { UpdateAnalysisConfigurationCommandGq } from '../../../graphql/projects/mutations/projects/useUpdateAnalysisConfigurationGq';
import { AnalysisListItem } from '../AnalysisList';

export interface AnalysisConfigurationRowProps {
  deletedIds: number[];
  charts: AnalysisListItem[];
  analysisConfiguration: AnalysisConfigurationGq;
  handleSelect: (analysisConfiguration: AnalysisConfigurationGq) => void;
  handleDelete: (analysisConfiguration: AnalysisConfigurationGq) => void;
  handleUpdate: (command: UpdateAnalysisConfigurationCommandGq) => void;
  editing: boolean;
  setEditId: (id?: number) => void;
  loading: boolean;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    tableRow: {
      cursor: 'pointer',
    },
    tableRowDisabled: {
      backgroundColor: theme.palette.grey[50],
    },
    tableCellDisabled: {
      color: theme.palette.text.disabled,
    },
    textField: {
      paddingBottom: 12,
      paddingTop: 11.5,
      paddingLeft: 4,
      paddingRight: 4,
      ...theme.typography.body2,
    },
  })
);

export const AnalysisConfigurationRow: React.FC<AnalysisConfigurationRowProps> = ({
  deletedIds,
  charts,
  analysisConfiguration,
  handleSelect,
  handleDelete,
  loading,
  editing,
  setEditId,
  handleUpdate,
}) => {
  const classes = useClasses();
  const [editState, setEditState] = useState(analysisConfiguration.title);
  useEffect(() => setEditState(analysisConfiguration.title), [analysisConfiguration.title]);

  if (deletedIds.includes(analysisConfiguration.id)) return null;
  const disabled = !charts.some(
    (al) => al.downloadId === analysisConfiguration.chartId && !al.disabled
  );

  return (
    <TableRow
      key={analysisConfiguration.id}
      onClick={disabled || editing ? undefined : () => handleSelect(analysisConfiguration)}
      hover={!disabled && !editing}
      className={disabled ? classes.tableRowDisabled : classes.tableRow}
    >
      {editing && (
        <>
          <TableCell colSpan={2} padding="checkbox">
            <form
              onSubmit={(event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
            >
              <TextField
                value={editState}
                onChange={(event) => setEditState(event.target.value)}
                autoFocus
                size="small"
                fullWidth
                InputProps={{
                  classes: { input: classes.textField },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        type="submit"
                        onClick={(event) => {
                          setEditId(undefined);
                          handleUpdate({
                            projectId: analysisConfiguration.projectId,
                            analysisConfigurationId: analysisConfiguration.id,
                            title: editState,
                            analysisFilter: analysisConfiguration.analysisFilter,
                          });
                          event.stopPropagation();
                          event.preventDefault();
                        }}
                      >
                        <Check fontSize="small" color="secondary" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          setEditId(undefined);
                          setEditState(analysisConfiguration.title);
                          event.stopPropagation();
                          event.preventDefault();
                        }}
                      >
                        <Cancel fontSize="small" color="error" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </form>
          </TableCell>
        </>
      )}
      {!editing && (
        <>
          <TableCell className={disabled ? classes.tableCellDisabled : undefined}>
            {editState}
          </TableCell>
          <TableCell align="right" padding="checkbox">
            <Grid container alignItems="center" justifyContent="center" wrap="nowrap">
              <Grid item>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setEditId(analysisConfiguration.id);
                  }}
                  disabled={loading}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    handleDelete(analysisConfiguration);
                  }}
                  disabled={loading}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

export default AnalysisConfigurationRow;
